import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Users, Mail, Lock, ArrowRight, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Login = () => {
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'sales'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const { login, setUser, setToken } = useAuth();
  const navigate = useNavigate();

  // Admin login with password
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  // Request OTP for sales login
  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/auth/otp/request`, { email });
      setOtpSent(true);
      setOtpEmail(response.data.email_sent_to);
      toast.success(`OTP sent to ${response.data.email_sent_to}`);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(errorMessage);
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP and login
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/otp/verify`, { email, otp });
      const { token, user } = response.data;
      
      // Set auth state
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setToken(token);
      setUser(user);
      
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid OTP';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Reset sales login state
  const handleResetOtp = () => {
    setOtpSent(false);
    setOtp('');
    setOtpEmail('');
  };

  // Switch login type
  const switchLoginType = (type) => {
    setLoginType(type);
    setEmail('');
    setPassword('');
    setOtp('');
    setOtpSent(false);
    setOtpEmail('');
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Image Section - Hidden on mobile, shown on lg+ */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1722942432154-067a239bc58d?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Minimalist closet"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 to-transparent"></div>
      </div>
      
      {/* Login Form Section */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white min-h-screen lg:min-h-0">
        <div className="w-full max-w-sm sm:max-w-md space-y-6 sm:space-y-8 py-8 sm:py-12">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to JSDC
            </h2>
            <p className="mt-2 sm:mt-3 text-base sm:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              B2B Order Management System
            </p>
          </div>

          {/* Login Type Tabs */}
          <div className="flex rounded-lg bg-slate-100 p-1">
            <button
              onClick={() => switchLoginType('admin')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                loginType === 'admin'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Shield className="w-4 h-4" />
              Admin Login
            </button>
            <button
              onClick={() => switchLoginType('sales')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                loginType === 'sales'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="w-4 h-4" />
              Sales Login (OTP)
            </button>
          </div>
          
          {/* Admin Login Form */}
          {loginType === 'admin' && (
            <form className="mt-6 sm:mt-8 space-y-5 sm:space-y-6" onSubmit={handleAdminLogin} data-testid="login-form">
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      data-testid="email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none relative block w-full pl-10 pr-4 py-3 border border-slate-200 placeholder-slate-400 text-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-base"
                      placeholder="admin@company.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      data-testid="password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="appearance-none relative block w-full pl-10 pr-4 py-3 border border-slate-200 placeholder-slate-400 text-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-base"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-slate-900 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Sales Login Form (OTP) */}
          {loginType === 'sales' && !otpSent && (
            <form className="mt-6 sm:mt-8 space-y-5 sm:space-y-6" onSubmit={handleRequestOtp}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="sales-email" className="block text-sm font-medium text-slate-700 mb-1">
                    Sales Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      id="sales-email"
                      name="email"
                      type="email"
                      required
                      data-testid="sales-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="appearance-none relative block w-full pl-10 pr-4 py-3 border border-slate-200 placeholder-slate-400 text-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-base"
                      placeholder="sales@company.com"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> An OTP will be sent to the admin email for verification.
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  data-testid="request-otp-button"
                  disabled={sendingOtp}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                >
                  {sendingOtp ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      Request OTP
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* OTP Verification Form */}
          {loginType === 'sales' && otpSent && (
            <form className="mt-6 sm:mt-8 space-y-5 sm:space-y-6" onSubmit={handleVerifyOtp}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <p className="text-sm text-emerald-800">
                  OTP sent to <strong>{otpEmail}</strong>
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Valid for 5 minutes • Max 3 attempts
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-slate-700 mb-1">
                    Enter OTP
                  </label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    data-testid="otp-input"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="appearance-none relative block w-full px-4 py-4 border border-slate-200 placeholder-slate-400 text-slate-900 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-2xl text-center tracking-[0.5em] font-mono"
                    placeholder="000000"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <button
                  type="submit"
                  data-testid="verify-otp-button"
                  disabled={loading || otp.length !== 6}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify & Login
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleResetOtp}
                  className="w-full py-2 px-4 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  ← Back to email entry
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
